import React, { useEffect, useCallback } from 'react';
import { withAppConfig } from '@appConfigContext';
import { withFeatureFlags } from '@withFeatureFlags';
import { compose } from 'recompose';
import ImagesPopup from './components/Popup';
import { ProImagesProps } from './components/types';
import { useProImages } from './components/hooks/useProImages';
import { useProImagesRedirect } from './components/hooks/useProImagesRedirect';
import { getDefaultProImageAPIConfig } from './components/config/apiConfig';
import { TITLES } from './components/Popup/constants';
import appConstants from '@appConstants';

/**
 * Generic component for displaying images from RouteMax backend API
 * Supports configurable API endpoints and response transformers
 * 
 * For integration guide and API examples, see: product/features/ProImages/frontend_PRO_IMAGES.md
 */
interface ComponentProps extends ProImagesProps {
  featureFlags?: {
    featureFlags?: Array<{ id: string; value: boolean }>;
    isFeatureEnabled?: (flagId: string) => boolean;
  };
  configJSON?: {
    enableDispatch?: boolean;
    proImagesUrl?: string;
    proMinLengthForImage?: number;
  };
}

function ProImages({ 
  show, 
  proNumber, 
  onClose,
  configJSON,
  apiConfig,
  useDefaultProImageAPIConfig,
  featureFlags
}: ComponentProps) {
  const isDispatchEnabled = Boolean(configJSON?.enableDispatch);

  // Reporting API (/api/reporting/GetImagePaths) is only available when dispatch is enabled
  const effectiveApiConfig = isDispatchEnabled
    ? (apiConfig || (useDefaultProImageAPIConfig ? getDefaultProImageAPIConfig() : undefined))
    : undefined;

  const showProImagesSection = Boolean(effectiveApiConfig);

  const { imageGroups, loading, error, fetchImages } = useProImages(
    effectiveApiConfig, 
    proNumber
  );

  const showProImagesInPopup = featureFlags?.featureFlags?.find(
    (ele) => ele.id === appConstants.features.ShowProImagesInPopup
  )?.value || featureFlags?.isFeatureEnabled?.(appConstants.features.ShowProImagesInPopup);

  // Fetch API images only when dialog mounts (see onFetchOnMount in ImagesPopup), not before dialog is open
  const shouldFetchOnMount = Boolean(effectiveApiConfig && !configJSON?.proImagesUrl);

  // Show popup or open in new tab based on feature flag (SMT, we open in new tab if feature flag is disabled)
  useProImagesRedirect({
    show,
    showProImagesInPopup,
    configJSON,
    proNumber,
    effectiveApiConfig,
    onClose
  });

  const handleRetry = useCallback(() => {
    if (effectiveApiConfig) {
      fetchImages();
    }
  }, [fetchImages, effectiveApiConfig]);

  const handleLoadProImages = useCallback(() => {
    if (effectiveApiConfig) {
      fetchImages();
    }
  }, [fetchImages, effectiveApiConfig]);

  if (!show) {
    return null;
  }

  // Only apply ShowProImagesInPopup = false when proImagesUrl is present (then open in new tab via redirect).
  if (showProImagesInPopup === false && configJSON?.proImagesUrl) {
    return null;
  }

  const title = TITLES.IMAGES_FOR_PRO(proNumber || '');

  // Determine if Pro Images should be manually loaded (when iframe exists but API not fetched)
  const shouldShowManualLoad = configJSON?.proImagesUrl && effectiveApiConfig && imageGroups.length === 0 && !loading && !error;

  return (
    <ImagesPopup
      open={show}
      title={title}
      handleClose={onClose}
      imageGroups={imageGroups}
      loading={loading}
      error={error}
      onRetry={handleRetry}
      onLoadProImages={shouldShowManualLoad ? handleLoadProImages : undefined}
      onFetchOnMount={shouldFetchOnMount ? fetchImages : undefined}
      showProImagesSection={showProImagesSection}
      configJSON={configJSON}
      proNumber={proNumber}
    />
  );
}

const WrappedComponent = compose(withAppConfig, withFeatureFlags)(ProImages as React.ComponentType<any>);
export default WrappedComponent;
